// ─── Canonical configurable-dashboard contract (Phase 0) ──────────────────────
// SINGLE source of truth for the persona set, the widget catalog, the saved-layout
// shape, and the reconcile/validate functions. The backend validation
// (PUT /api/dashboard/layout, profile hydrate, the user-aware GET /api/dashboard),
// the frontend registry/picker, and the AI layout generator ALL import from here.
// No slice re-declares any of these types. Build @pcp/shared first — it gates
// every other slice of the feature.
//
// See `_plan_dashboard.md` §1–§2 for the authoritative spec this file implements.
export const PERSONA_ORDER = [
    'product-management',
    'support',
    'devops',
    'development',
    'sales',
    'administration',
];
export const PERSONAS = {
    'product-management': {
        key: 'product-management',
        label: 'Product Management',
        description: 'Backlog health, releases, insights and the knowledge that feeds them.',
        icon: 'feature',
        accent: '--accent-primary',
    },
    support: {
        key: 'support',
        label: 'Support',
        description: 'Incoming tickets, forum topics and the live support pulse.',
        icon: 'support',
        accent: '--accent-info',
    },
    devops: {
        key: 'devops',
        label: 'DevOps',
        description: 'Sync health, system status, releases and deploy activity.',
        icon: 'tools',
        accent: '--accent-success',
    },
    development: {
        key: 'development',
        label: 'Development',
        description: 'Commits, work items and the knowledge index behind them.',
        icon: 'build',
        accent: '--accent-warning',
    },
    sales: {
        key: 'sales',
        label: 'Sales',
        description: 'Customer-facing insights, support pulse and what is shipping.',
        icon: 'handshake',
        accent: '--accent-primary',
    },
    administration: {
        key: 'administration',
        label: 'Administration',
        description: 'AI spend, system health, releases and operational oversight.',
        icon: 'settings',
        accent: '--accent-danger',
    },
};
/** Desktop: a 12-column grid (spans 3 / 4 / 6 / 12). */
export const WIDTH_SPANS = { small: 3, medium: 4, large: 6, full: 12 };
/** Mobile: a 2-column grid — small/medium = half, large/full = full-width. */
export const WIDTH_SPANS_MOBILE = { small: 1, medium: 1, large: 2, full: 2 };
const WIDGET_WIDTHS = ['small', 'medium', 'large', 'full'];
function isWidgetWidth(x) {
    return typeof x === 'string' && WIDGET_WIDTHS.includes(x);
}
// ─── Reusable setting fields ──────────────────────────────────────────────────
const ITEM_COUNT_SETTING = {
    kind: 'number',
    key: 'itemCount',
    label: 'Items to show',
    min: 3,
    max: 15,
    step: 1,
    default: 5,
};
const WINDOW_SETTING = {
    kind: 'enum',
    key: 'window',
    label: 'Time window',
    options: [
        { value: '7', label: 'Last 7 days' },
        { value: '14', label: 'Last 14 days' },
        { value: '30', label: 'Last 30 days' },
    ],
    default: '14',
};
// ─── Widget catalog (§2, ~26 widgets) ─────────────────────────────────────────
// `requiredPermission` is intentionally absent on every read-only widget whose
// backing GET route is OPEN (insights/*, employees, usage/*, dashboard, zoho/
// azure/commits list GETs). Over-gating would hide usable widgets in the picker.
export const DASHBOARD_WIDGETS = [
    // ── Inbox / "New" ──
    {
        id: 'new-discourse',
        title: 'New forum topics',
        description: 'Unreviewed Discourse topics, newest first.',
        category: 'inbox',
        icon: 'new',
        defaultWidthDesktop: 'medium',
        defaultWidthMobile: 'full',
        personas: ['support', 'product-management'],
        dataSource: { kind: 'dashboard', field: 'newItems' },
        settings: [ITEM_COUNT_SETTING],
        defaultVisibleDesktop: true,
        defaultVisibleMobile: true,
    },
    {
        id: 'new-zoho',
        title: 'New tickets',
        description: 'Unreviewed Zoho Desk tickets, newest first.',
        category: 'inbox',
        icon: 'tickets',
        defaultWidthDesktop: 'medium',
        defaultWidthMobile: 'full',
        personas: ['support'],
        dataSource: { kind: 'dashboard', field: 'newItems' },
        settings: [ITEM_COUNT_SETTING],
        defaultVisibleDesktop: true,
        defaultVisibleMobile: true,
    },
    {
        id: 'new-azure',
        title: 'New work items',
        description: 'Unreviewed Azure DevOps work items, newest first.',
        category: 'inbox',
        icon: 'backlog',
        defaultWidthDesktop: 'medium',
        defaultWidthMobile: 'full',
        personas: ['development', 'product-management'],
        dataSource: { kind: 'dashboard', field: 'newItems' },
        settings: [ITEM_COUNT_SETTING],
        defaultVisibleDesktop: true,
        defaultVisibleMobile: true,
    },
    {
        id: 'new-commits',
        title: 'New commits',
        description: 'Recently synced git commits awaiting review.',
        category: 'inbox',
        icon: 'commits',
        defaultWidthDesktop: 'medium',
        defaultWidthMobile: 'full',
        personas: ['development', 'devops'],
        dataSource: { kind: 'dashboard', field: 'newItems' },
        settings: [ITEM_COUNT_SETTING],
        defaultVisibleDesktop: true,
        defaultVisibleMobile: true,
    },
    // ── Composite KPI strip (pinned default, all personas) ──
    {
        id: 'stat-strip',
        title: 'Key metrics',
        description: 'Composite KPI tiles across every connected source.',
        category: 'insights',
        icon: 'chart',
        defaultWidthDesktop: 'full',
        defaultWidthMobile: 'full',
        personas: ['product-management', 'support', 'devops', 'development', 'sales', 'administration'],
        dataSource: { kind: 'dashboard', field: 'newItems' },
        defaultVisibleDesktop: true,
        defaultVisibleMobile: true,
    },
    // ── Operations (dashboard-backed) ──
    {
        id: 'recent-activity',
        title: 'Recent activity',
        description: 'Latest sync runs across all integrations.',
        category: 'operations',
        icon: 'refresh',
        defaultWidthDesktop: 'large',
        defaultWidthMobile: 'full',
        personas: ['devops'],
        dataSource: { kind: 'dashboard', field: 'recentSyncs' },
        settings: [ITEM_COUNT_SETTING],
        defaultVisibleDesktop: true,
        defaultVisibleMobile: false,
    },
    {
        id: 'system-health',
        title: 'System health',
        description: 'Database, Redis, OpenAI and Ollama service status.',
        category: 'operations',
        icon: 'status',
        defaultWidthDesktop: 'small',
        defaultWidthMobile: 'small',
        personas: ['devops', 'administration'],
        dataSource: { kind: 'dashboard', field: 'systemHealth' },
        defaultVisibleDesktop: true,
        defaultVisibleMobile: false,
    },
    {
        id: 'sync-health',
        title: 'Sync health',
        description: 'Per-integration sync freshness and current state.',
        category: 'operations',
        icon: 'refresh',
        defaultWidthDesktop: 'medium',
        defaultWidthMobile: 'full',
        personas: ['devops'],
        dataSource: { kind: 'dashboard', field: 'syncHealth' },
        defaultVisibleDesktop: true,
        defaultVisibleMobile: false,
    },
    {
        id: 'sync-activity',
        title: 'Sync activity',
        description: 'Completed sync runs per day over a recent window.',
        category: 'operations',
        icon: 'chart',
        defaultWidthDesktop: 'medium',
        defaultWidthMobile: 'full',
        personas: ['devops'],
        dataSource: { kind: 'dashboard', field: 'syncActivity' },
        settings: [WINDOW_SETTING],
        defaultVisibleDesktop: true,
        defaultVisibleMobile: false,
    },
    // ── Knowledge (dashboard-backed) — §2 inversion fix ──
    {
        id: 'knowledge-index',
        title: 'Knowledge index',
        description: 'Embedding coverage across the indexed corpus.',
        category: 'knowledge',
        icon: 'sources',
        defaultWidthDesktop: 'medium',
        defaultWidthMobile: 'full',
        personas: ['development', 'product-management'],
        dataSource: { kind: 'dashboard', field: 'knowledge' },
        requiredFeature: 'knowledge',
        defaultVisibleDesktop: true,
        defaultVisibleMobile: false,
    },
    {
        id: 'knowledge-extraction',
        title: 'Knowledge extraction',
        description: 'Image and attachment extraction coverage per integration.',
        category: 'knowledge',
        icon: 'image',
        defaultWidthDesktop: 'medium',
        defaultWidthMobile: 'full',
        personas: ['development'],
        dataSource: { kind: 'dashboard', field: 'knowledgeCoverage' },
        requiredFeature: 'knowledgeCoverage',
        defaultVisibleDesktop: true,
        defaultVisibleMobile: false,
    },
    // ── AI / images (dashboard-backed) ──
    {
        id: 'ai-usage',
        title: 'AI usage',
        description: 'Token spend over a recent window, with a sparkline.',
        category: 'ai',
        icon: 'credits',
        defaultWidthDesktop: 'medium',
        defaultWidthMobile: 'full',
        personas: ['administration', 'devops'],
        dataSource: { kind: 'dashboard', field: 'aiUsage' },
        requiredFeature: 'aiUsage',
        defaultVisibleDesktop: true,
        defaultVisibleMobile: false,
    },
    {
        id: 'images-cache',
        title: 'Image cache',
        description: 'ImgProxy cached-image footprint and fetch errors.',
        category: 'operations',
        icon: 'image',
        defaultWidthDesktop: 'small',
        defaultWidthMobile: 'small',
        personas: ['administration'],
        dataSource: { kind: 'dashboard', field: 'images' },
        requiredFeature: 'images',
        defaultVisibleDesktop: true,
        defaultVisibleMobile: false,
    },
    // ── Support (dashboard-backed) ──
    {
        id: 'support-pulse',
        title: 'Support pulse',
        description: 'Ticket volume, open/unreviewed counts and response averages.',
        category: 'support',
        icon: 'support',
        defaultWidthDesktop: 'medium',
        defaultWidthMobile: 'full',
        personas: ['support', 'sales'],
        dataSource: { kind: 'dashboard', field: 'support' },
        requiredFeature: 'support',
        defaultVisibleDesktop: true,
        defaultVisibleMobile: true,
    },
    // ── Releases (dashboard-backed) ──
    {
        id: 'releases-board',
        title: 'Releases',
        description: 'Current release train plus ongoing and last deploys.',
        category: 'releases',
        icon: 'releases',
        defaultWidthDesktop: 'large',
        defaultWidthMobile: 'full',
        personas: ['devops', 'product-management', 'administration'],
        dataSource: { kind: 'dashboard', field: 'releases' },
        requiredFeature: 'releases',
        defaultVisibleDesktop: true,
        defaultVisibleMobile: true,
    },
    // ── Documents (dashboard-backed) — show empty-state, don't hard-hide at 0 ──
    {
        id: 'documents',
        title: 'Documents',
        description: 'Documentation knowledge-base prepared / pending counts.',
        category: 'knowledge',
        icon: 'documentation',
        defaultWidthDesktop: 'small',
        defaultWidthMobile: 'small',
        personas: ['development', 'product-management'],
        dataSource: { kind: 'dashboard', field: 'documents' },
        requiredFeature: 'documents',
        defaultVisibleDesktop: true,
        defaultVisibleMobile: false,
    },
    // ── Endpoint-backed (lazy client fetch) ──
    {
        id: 'preparing-release',
        title: 'Preparing a release',
        description: 'Live banner for in-progress release-preparation sessions.',
        category: 'releases',
        icon: 'rocket',
        defaultWidthDesktop: 'full',
        defaultWidthMobile: 'full',
        personas: ['devops', 'administration'],
        dataSource: { kind: 'endpoint', path: '/api/releases/sessions' },
        requiredFeature: 'releases',
        defaultVisibleDesktop: true,
        defaultVisibleMobile: true,
    },
    {
        id: 'my-open-tickets',
        title: 'My open tickets',
        description: 'Zoho Desk tickets currently assigned to you.',
        category: 'support',
        icon: 'tickets',
        defaultWidthDesktop: 'medium',
        defaultWidthMobile: 'full',
        personas: ['support'],
        dataSource: { kind: 'endpoint', path: '/api/zoho/tickets' },
        requiredFeature: 'support',
        settings: [ITEM_COUNT_SETTING],
        defaultVisibleDesktop: true,
        defaultVisibleMobile: true,
    },
    {
        id: 'backlog-snapshot',
        title: 'Backlog snapshot',
        description: 'A snapshot of recent Azure DevOps work items.',
        category: 'operations',
        icon: 'backlog',
        defaultWidthDesktop: 'large',
        defaultWidthMobile: 'full',
        personas: ['development', 'product-management'],
        dataSource: { kind: 'endpoint', path: '/api/azure/work-items' },
        settings: [ITEM_COUNT_SETTING],
        defaultVisibleDesktop: true,
        defaultVisibleMobile: false,
    },
    {
        id: 'recent-commits',
        title: 'Recent commits',
        description: 'The latest git commits with message and changed files.',
        category: 'operations',
        icon: 'commits',
        defaultWidthDesktop: 'medium',
        defaultWidthMobile: 'full',
        personas: ['development', 'devops'],
        dataSource: { kind: 'endpoint', path: '/api/commits' },
        settings: [ITEM_COUNT_SETTING],
        defaultVisibleDesktop: true,
        defaultVisibleMobile: false,
    },
    {
        id: 'entitlement-review-queue',
        title: 'Entitlement review queue',
        description: 'AI-suggested entitlements awaiting accept or reject.',
        category: 'releases',
        icon: 'entitlements',
        defaultWidthDesktop: 'medium',
        defaultWidthMobile: 'full',
        personas: ['product-management', 'administration'],
        dataSource: { kind: 'endpoint', path: '/api/entitlements/suggestions' },
        requiredFeature: 'entitlements',
        requiredPermission: 'entitlements.manage',
        settings: [ITEM_COUNT_SETTING],
        defaultVisibleDesktop: true,
        defaultVisibleMobile: false,
    },
    {
        id: 'release-status-board',
        title: 'Release status board',
        description: 'Per-app int/prod versions and out-of-sync apps.',
        category: 'releases',
        icon: 'releases',
        defaultWidthDesktop: 'large',
        defaultWidthMobile: 'full',
        personas: ['devops', 'administration'],
        dataSource: { kind: 'endpoint', path: '/api/releases/status' },
        requiredFeature: 'releases',
        defaultVisibleDesktop: true,
        defaultVisibleMobile: false,
    },
    {
        id: 'employee-activity',
        title: 'Employee activity',
        description: 'Recent commits and work-item activity per employee.',
        category: 'insights',
        icon: 'users',
        defaultWidthDesktop: 'medium',
        defaultWidthMobile: 'full',
        personas: ['administration', 'development'],
        dataSource: { kind: 'endpoint', path: '/api/employees' },
        requiredFeature: 'employees',
        settings: [ITEM_COUNT_SETTING],
        defaultVisibleDesktop: true,
        defaultVisibleMobile: false,
    },
    {
        id: 'knowledge-activity',
        title: 'Knowledge activity',
        description: 'Recent embedding and indexing activity.',
        category: 'knowledge',
        icon: 'learning',
        defaultWidthDesktop: 'medium',
        defaultWidthMobile: 'full',
        personas: ['development'],
        dataSource: { kind: 'endpoint', path: '/api/knowledge/activity' },
        requiredFeature: 'knowledge',
        defaultVisibleDesktop: true,
        defaultVisibleMobile: false,
    },
    {
        id: 'ai-impact',
        title: 'AI impact',
        description: 'Acceptance and edit rates of AI-assisted actions.',
        category: 'ai',
        icon: 'ai',
        defaultWidthDesktop: 'medium',
        defaultWidthMobile: 'full',
        personas: ['administration', 'product-management'],
        dataSource: { kind: 'endpoint', path: '/api/insights/ai' },
        requiredFeature: 'insights',
        requiredAiFeature: 'aiAnalysis',
        defaultVisibleDesktop: true,
        defaultVisibleMobile: false,
    },
    {
        id: 'insights-kpis',
        title: 'Insights KPIs',
        description: 'Headline org metrics with period-over-period deltas.',
        category: 'insights',
        icon: 'insights',
        defaultWidthDesktop: 'large',
        defaultWidthMobile: 'full',
        personas: ['product-management', 'sales', 'administration'],
        dataSource: { kind: 'endpoint', path: '/api/insights/overview' },
        requiredFeature: 'insights',
        defaultVisibleDesktop: true,
        defaultVisibleMobile: true,
    },
    {
        id: 'support-chat-pulse',
        title: 'Support chat pulse',
        description: 'Active and escalated customer support-chat threads.',
        category: 'support',
        icon: 'support',
        defaultWidthDesktop: 'medium',
        defaultWidthMobile: 'full',
        personas: ['support'],
        dataSource: { kind: 'endpoint', path: '/api/support-chat/chats' },
        requiredFeature: 'support',
        defaultVisibleDesktop: true,
        defaultVisibleMobile: false,
    },
    // ── Static (no fetch) ──
    {
        id: 'quick-links',
        title: 'Quick links',
        description: 'Your own pinned links, kept on this dashboard.',
        category: 'utility',
        icon: 'link',
        defaultWidthDesktop: 'small',
        defaultWidthMobile: 'small',
        personas: ['product-management', 'support', 'devops', 'development', 'sales', 'administration'],
        dataSource: { kind: 'static' },
        settings: [
            {
                kind: 'text',
                key: 'links',
                label: 'Links (one "Label | https://url" per line)',
                maxLength: 4000,
                default: '',
            },
        ],
        defaultVisibleDesktop: true,
        defaultVisibleMobile: true,
    },
    {
        id: 'notes',
        title: 'Notes',
        description: 'A free-text scratchpad pinned to your dashboard.',
        category: 'utility',
        icon: 'note',
        defaultWidthDesktop: 'small',
        defaultWidthMobile: 'medium',
        personas: ['product-management', 'support', 'devops', 'development', 'sales', 'administration'],
        dataSource: { kind: 'static' },
        settings: [
            { kind: 'text', key: 'body', label: 'Notes', maxLength: 4000, default: '' },
        ],
        defaultVisibleDesktop: true,
        defaultVisibleMobile: false,
    },
];
export const WIDGETS_BY_ID = new Map(DASHBOARD_WIDGETS.map((w) => [w.id, w]));
export function getWidget(id) {
    return WIDGETS_BY_ID.get(id);
}
export const DASHBOARD_LAYOUT_VERSION = 1;
const PERMISSION_WILDCARD = '*';
/** A widget is eligible when the caller has its permission (if any), the required
 *  feature is configured (if any), and — for AI widgets — they hold `ai.use`. */
export function isWidgetEligible(d, ctx) {
    const perms = ctx.permissions ?? [];
    const has = (key) => perms.includes(PERMISSION_WILDCARD) || perms.includes(key);
    if (d.requiredPermission && !has(d.requiredPermission))
        return false;
    if (d.requiredFeature && ctx.features[d.requiredFeature] !== true)
        return false;
    // Any AI-backed widget additionally requires the `ai.use` grant (token spend).
    if (d.requiredAiFeature && !has('ai.use'))
        return false;
    return true;
}
// ─── Settings clamp ───────────────────────────────────────────────────────────
/** Coerce an arbitrary settings object to the descriptor's declared fields:
 *  every declared field gets a valid value; unknown keys are dropped. */
export function clampWidgetSettings(d, raw) {
    const out = {};
    const fields = d.settings ?? [];
    if (fields.length === 0)
        return out;
    const src = raw && typeof raw === 'object' ? raw : {};
    for (const f of fields) {
        const v = src[f.key];
        switch (f.kind) {
            case 'number': {
                let n = typeof v === 'number' && Number.isFinite(v) ? v : f.default;
                if (n < f.min)
                    n = f.min;
                if (n > f.max)
                    n = f.max;
                if (f.step && f.step > 0) {
                    // Snap to the nearest step from `min`, then re-clamp.
                    n = f.min + Math.round((n - f.min) / f.step) * f.step;
                    if (n < f.min)
                        n = f.min;
                    if (n > f.max)
                        n = f.max;
                }
                out[f.key] = n;
                break;
            }
            case 'enum': {
                out[f.key] = typeof v === 'string' && f.options.some((o) => o.value === v) ? v : f.default;
                break;
            }
            case 'boolean': {
                out[f.key] = typeof v === 'boolean' ? v : f.default;
                break;
            }
            case 'text': {
                const s = typeof v === 'string' ? v : f.default;
                out[f.key] = s.length > f.maxLength ? s.slice(0, f.maxLength) : s;
                break;
            }
        }
    }
    return out;
}
// ─── Placement helpers ────────────────────────────────────────────────────────
/** A descriptor's optimal default placement on both breakpoints. */
function defaultPlacement(d) {
    const pw = {
        id: d.id,
        widthDesktop: d.defaultWidthDesktop,
        widthMobile: d.defaultWidthMobile,
        visibleOnDesktop: d.defaultVisibleDesktop,
        visibleOnMobile: d.defaultVisibleMobile,
    };
    if (d.settings && d.settings.length > 0)
        pw.settings = clampWidgetSettings(d, {});
    return pw;
}
/** Coerce one placed-widget entry against its descriptor (widths, visibility,
 *  settings), falling back to the descriptor's optimal defaults. */
function coercePlacement(d, raw) {
    const base = defaultPlacement(d);
    if (!raw)
        return base;
    const pw = {
        id: d.id,
        widthDesktop: isWidgetWidth(raw.widthDesktop) ? raw.widthDesktop : base.widthDesktop,
        widthMobile: isWidgetWidth(raw.widthMobile) ? raw.widthMobile : base.widthMobile,
        visibleOnDesktop: typeof raw.visibleOnDesktop === 'boolean' ? raw.visibleOnDesktop : base.visibleOnDesktop,
        visibleOnMobile: typeof raw.visibleOnMobile === 'boolean' ? raw.visibleOnMobile : base.visibleOnMobile,
    };
    if (d.settings && d.settings.length > 0)
        pw.settings = clampWidgetSettings(d, raw.settings);
    return pw;
}
function normalizePersonas(raw) {
    if (!Array.isArray(raw))
        return [];
    const seen = new Set();
    for (const p of raw) {
        if (typeof p === 'string' && PERSONA_ORDER.includes(p)) {
            seen.add(p);
        }
    }
    // Keep canonical persona order.
    return PERSONA_ORDER.filter((p) => seen.has(p));
}
function normalizeDensity(raw) {
    return raw === 'comfortable' || raw === 'compact' ? raw : undefined;
}
// ─── Reconcile / validate pipeline ────────────────────────────────────────────
// ONE pipeline used by the API PUT, profile hydrate, client read, and AI
// post-parse. `reconcileLayout` is the trusted-shape variant; `validateIncomingLayout`
// is the untrusted-input variant — both funnel through `reconcileFrom`.
function reconcileFrom(personasRaw, densityRaw, onboardedAtRaw, widgetsRaw, ctx) {
    const personas = normalizePersonas(personasRaw);
    const widgets = [];
    const placed = new Set();
    // 1. Keep saved widgets that still exist AND are eligible — preserving order.
    if (Array.isArray(widgetsRaw)) {
        for (const entry of widgetsRaw) {
            if (!entry || typeof entry !== 'object')
                continue;
            const id = entry.id;
            if (typeof id !== 'string' || placed.has(id))
                continue;
            const d = WIDGETS_BY_ID.get(id);
            if (!d || !isWidgetEligible(d, ctx))
                continue;
            widgets.push(coercePlacement(d, entry));
            placed.add(id);
        }
    }
    // 2. Append any newly-eligible catalog widget for the user's personas that the
    //    saved layout never knew about (so a new widget surfaces at its defaults).
    if (personas.length > 0) {
        for (const d of DASHBOARD_WIDGETS) {
            if (placed.has(d.id))
                continue;
            if (!d.personas.some((p) => personas.includes(p)))
                continue;
            if (!isWidgetEligible(d, ctx))
                continue;
            widgets.push(defaultPlacement(d));
            placed.add(d.id);
        }
    }
    const layout = {
        version: DASHBOARD_LAYOUT_VERSION,
        personas,
        widgets,
    };
    const density = normalizeDensity(densityRaw);
    if (density)
        layout.density = density;
    if (typeof onboardedAtRaw === 'string' && onboardedAtRaw)
        layout.onboardedAt = onboardedAtRaw;
    return layout;
}
/** Trusted-shape reconcile: drop unknown/ineligible widgets, coerce widths +
 *  clamp settings, append newly-eligible defaults, migrate version. null ⇒ null
 *  (an un-onboarded user stays un-onboarded, surfacing the wizard). */
export function reconcileLayout(saved, ctx) {
    if (!saved) {
        return { version: DASHBOARD_LAYOUT_VERSION, personas: [], widgets: [] };
    }
    return reconcileFrom(saved.personas, saved.density, saved.onboardedAt, saved.widgets, ctx);
}
/** Untrusted-input variant — same pipeline, defensive about arbitrary shapes.
 *  Every layout WRITE (edit UI or AI) runs through this so neither can persist
 *  an ineligible/unknown widget. */
export function validateIncomingLayout(raw, ctx) {
    const obj = raw && typeof raw === 'object' ? raw : {};
    return reconcileFrom(obj.personas, obj.density, obj.onboardedAt, obj.widgets, ctx);
}
// Each persona's curated, ordered seed list. The default-layout algorithm unions
// the selected personas' seeds (first-wins/deduped) before appending the rest of
// their eligible catalog. Sales deliberately carries ≥4 eligible widgets.
export const PERSONA_SEEDS = {
    'product-management': [
        { widgetId: 'stat-strip' },
        { widgetId: 'insights-kpis' },
        { widgetId: 'new-azure' },
        { widgetId: 'backlog-snapshot' },
        { widgetId: 'releases-board' },
        { widgetId: 'knowledge-index' },
    ],
    support: [
        { widgetId: 'stat-strip' },
        { widgetId: 'new-zoho' },
        { widgetId: 'my-open-tickets' },
        { widgetId: 'support-pulse' },
        { widgetId: 'new-discourse' },
        { widgetId: 'support-chat-pulse' },
    ],
    devops: [
        { widgetId: 'stat-strip' },
        { widgetId: 'sync-health' },
        { widgetId: 'system-health' },
        { widgetId: 'recent-activity' },
        { widgetId: 'releases-board' },
        { widgetId: 'sync-activity' },
    ],
    development: [
        { widgetId: 'stat-strip' },
        { widgetId: 'new-commits' },
        { widgetId: 'recent-commits' },
        { widgetId: 'new-azure' },
        { widgetId: 'backlog-snapshot' },
        { widgetId: 'knowledge-index' },
    ],
    sales: [
        { widgetId: 'stat-strip' },
        { widgetId: 'insights-kpis' },
        { widgetId: 'support-pulse' },
        { widgetId: 'releases-board' },
        { widgetId: 'quick-links' },
        { widgetId: 'notes' },
    ],
    administration: [
        { widgetId: 'stat-strip' },
        { widgetId: 'system-health' },
        { widgetId: 'ai-usage' },
        { widgetId: 'releases-board' },
        { widgetId: 'insights-kpis' },
        { widgetId: 'images-cache' },
    ],
};
/** Apply a seed's overrides on top of the descriptor's optimal default placement. */
function placementFromSeed(d, seed) {
    return coercePlacement(d, {
        widthDesktop: seed.widthDesktop,
        widthMobile: seed.widthMobile,
        visibleOnDesktop: seed.visibleOnDesktop,
        visibleOnMobile: seed.visibleOnMobile,
    });
}
/**
 * THE single default-layout algorithm (used by the wizard, Reset-to-defaults, and
 * the AI fallback): iterate PERSONA_ORDER; union the selected personas' seeds
 * first-wins/deduped; filter by `isWidgetEligible`; then append any remaining
 * eligible catalog widgets for those personas at their per-breakpoint defaults.
 */
export function personaDefaultLayout(personas, ctx) {
    const selected = normalizePersonas(personas);
    const widgets = [];
    const placed = new Set();
    // 1. Seeds, in canonical persona order, first-wins/deduped, eligible-only.
    for (const persona of PERSONA_ORDER) {
        if (!selected.includes(persona))
            continue;
        for (const seed of PERSONA_SEEDS[persona]) {
            if (placed.has(seed.widgetId))
                continue;
            const d = WIDGETS_BY_ID.get(seed.widgetId);
            if (!d || !isWidgetEligible(d, ctx))
                continue;
            widgets.push(placementFromSeed(d, seed));
            placed.add(seed.widgetId);
        }
    }
    // 2. Append remaining eligible catalog widgets for the selected personas.
    for (const d of DASHBOARD_WIDGETS) {
        if (placed.has(d.id))
            continue;
        if (!d.personas.some((p) => selected.includes(p)))
            continue;
        if (!isWidgetEligible(d, ctx))
            continue;
        widgets.push(defaultPlacement(d));
        placed.add(d.id);
    }
    return {
        version: DASHBOARD_LAYOUT_VERSION,
        personas: selected,
        widgets,
    };
}
/** Wizard / reset entry point. MUST delegate to `personaDefaultLayout` so the
 *  wizard and "Reset to defaults" can never disagree. */
export function mergePersonaSeeds(personas, ctx) {
    return personaDefaultLayout(personas, ctx);
}
//# sourceMappingURL=dashboard.js.map